import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast } from "react-toastify";
import { ArrowRight, Building2, Gauge, Info, User } from "lucide-react";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { OrganizationSummary } from "../shared/Interfaces";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface Model {
  max_tokens: number;
  token_rate_limit_input: number;
  token_rate_limit_output: number;
  user_tier: string;
  requests_per_minute: number;
  [key: string]: any;
}
interface ModelList { [modelName: string]: Model }

const fmt = (rate: number): string => {
  if (rate >= 1_000_000) {
    const m = rate / 1_000_000;
    const f = m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(".", ",");
    return `${f}M`;
  }
  if (rate >= 1000) return `${(rate / 1000).toFixed(0)}K`;
  return rate.toString();
};

export default function RateLimits() {
  const [loading, setLoading] = useState<boolean>(true);
  const [models, setModels] = useState<ModelList>({});
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [scope, setScope] = useState<"personal" | "organization">("personal");
  const [activeOrganizationId, setActiveOrganizationId] = useState<string>("");
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const fetchOrganizations = useCallback(async () => {
    try {
      const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!token) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data["organizations"] ?? []);
      }
    } catch {
      // Silent: orgs are optional for this page.
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const fetchModels = useCallback(
    async (organizationId?: string) => {
      try {
        setLoading(true);
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const url = organizationId
          ? `${API_BASE}/api/chat/models?organization_id=${encodeURIComponent(organizationId)}`
          : `${API_BASE}/api/chat/models`;
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Auth ${token}` },
        });
        if (!response.ok) {
          reportApiError(response, { feature: "ratelimits.fetch", method: "GET" });
          throw new Error("Failed");
        }
        const data = await response.json();
        setModels(data["models"] || {});
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "ratelimits.fetch" });
        toast.error("Erro ao recuperar rate limits.", TOASTPROPS);
        setModels({});
      } finally {
        setLoading(false);
      }
    },
    [getAccessTokenSilently, loginWithRedirect]
  );

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (scope === "personal") {
      fetchModels();
    } else if (activeOrganizationId) {
      fetchModels(activeOrganizationId);
    }
  }, [scope, activeOrganizationId, fetchModels]);

  // If user has no orgs, force personal scope.
  useEffect(() => {
    if (scope === "organization" && organizations.length === 0) {
      setScope("personal");
      setActiveOrganizationId("");
    }
  }, [scope, organizations]);

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setScope("personal");
      setActiveOrganizationId("");
    } else {
      setScope("organization");
      setActiveOrganizationId(value);
    }
  };

  const scopeSelectValue = scope === "personal" ? "personal" : activeOrganizationId;
  const selectedOrgName = useMemo(
    () => organizations.find((o) => o.organization_id === activeOrganizationId)?.name,
    [organizations, activeOrganizationId]
  );

  const userTier = Object.values(models)[0]?.user_tier || "";
  const visible = Object.keys(models).filter((n) => !n.toLowerCase().includes("competidor"));

  return (
    <div className="space-y-4">
      {/* Scope selector — only render when user has at least one org */}
      {(
        <Card>
          <CardContent className="flex flex-col gap-3 p-5 lg:flex-row lg:items-center">
            <Label className="text-sm text-muted-foreground">Visualizar limites de:</Label>
            <Select value={scopeSelectValue || "personal"} onValueChange={handleScopeChange}>
              <SelectTrigger className="w-full lg:w-[280px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Minha conta (pessoal)
                  </span>
                </SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.organization_id} value={org.organization_id}>
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Org: {org.name ?? org.organization_id}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Plan card */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
              <Gauge className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Plano atual
                </p>
                <Badge variant="secondary">
                  {loading ? "..." : userTier || "—"}
                </Badge>
                {scope === "organization" && selectedOrgName && (
                  <Badge variant="outline" className="gap-1">
                    <Building2 className="h-3 w-3" />
                    {selectedOrgName}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {scope === "organization"
                  ? "Os limites abaixo se aplicam às chaves de API criadas a partir desta organização."
                  : "Os limites abaixo são personalizados para o seu nível de uso."}
              </p>
            </div>
          </div>
          <a
            href="https://docs.maritaca.ai/pt/rate-limits#n%C3%ADveis-de-uso"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 self-start whitespace-nowrap text-[13px] font-medium text-primary hover:underline sm:self-center"
          >
            Como subir de nível
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </CardContent>
      </Card>

      {/* Limits table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[740px] table-fixed text-sm">
            <colgroup>{[40, 20, 20, 20].map((width, i) => <col key={i} style={{ width: width + "%" }} />)}</colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th rowSpan={2} className="px-5 py-3 align-middle">Modelo</th>
                <th colSpan={2} className="border-l border-border px-5 py-2 text-center">
                  Tokens por minuto
                </th>
                <th rowSpan={2} className="border-l border-border px-5 py-3 text-right align-middle">
                  Requisições / min
                </th>
              </tr>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="border-l border-border px-5 py-2 text-right font-normal normal-case tracking-normal text-[10.5px]">
                  Input
                </th>
                <th className="px-5 py-2 text-right font-normal normal-case tracking-normal text-[10.5px]">
                  Output
                </th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="h-12 border-b border-border/60">
                      <td className="px-5 py-3"><Skeleton className="h-4 w-32" /></td>
                      <td className="border-l border-border px-5 py-3"><Skeleton className="ml-auto h-4 w-12" /></td>
                      <td className="px-5 py-3"><Skeleton className="ml-auto h-4 w-12" /></td>
                      <td className="border-l border-border px-5 py-3"><Skeleton className="ml-auto h-4 w-10" /></td>
                    </tr>
                  ))
                : visible.map((name) => {
                    const m = models[name];
                    return (
                      <tr key={name} className="h-12 border-b border-border/60 transition-colors hover:bg-muted/30">
                        <td className="px-5 py-3 font-mono text-[13px] font-semibold text-foreground">
                          {name}
                        </td>
                        <td className="border-l border-border px-5 py-3 text-right font-mono text-[13px] text-foreground">
                          {fmt(m.token_rate_limit_input)}
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-[13px] text-foreground">
                          {fmt(m.token_rate_limit_output)}
                        </td>
                        <td className="border-l border-border px-5 py-3 text-right font-mono text-[13px] text-foreground">
                          {m.requests_per_minute}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Explainer */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-[13px] font-semibold text-foreground">Tokens por minuto (TPM)</p>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Quantidade total de tokens (input + output, separadamente) que sua chave pode
              processar em uma janela de 60 segundos.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-[13px] font-semibold text-foreground">Requisições por minuto (RPM)</p>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Número máximo de chamadas à API que sua chave pode fazer em uma janela de 60
              segundos. Excesso retorna HTTP 429.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}